/**
 * Entity Association Types
 *
 * Defines the types of relationships/associations between entities in the system.
 * Each association type may have specific rules about which entity types can be linked.
 */
export enum EntityAssociationType {
  /**
   * AP Invoice Matching
   *
   * Used for matching invoice line items to purchase order line items or sales order charges
   * during reconciliation.
   *
   * Allowed entity type pairs:
   * - AP_INVOICE → INTERNAL_PURCHASE_ORDER (header-level link)
   * - AP_INVOICE → AR_SALES_ORDER (header-level link for SO charges)
   * - AP_INVOICE → SCM_SHIPMENT (header-level link for shipment charges)
   * - AP_INVOICE_LINE_ITEM → INTERNAL_PURCHASE_ORDER_LINE_ITEM (line-level match)
   * - AP_INVOICE_LINE_ITEM → CHARGE (match to PO, SO, or Shipment charges)
   * - AP_INVOICE_LINE_ITEM → AR_SALES_ORDER_LINE_ITEM (SO line match)
   */
  AP_INVOICE_MATCHING = "AP_INVOICE_MATCHING",

  /**
   * Activity Graph
   *
   * Used for linking entities that are related in the context of activity tracking.
   * Creates a graph of related entities that should appear together in activity logs.
   *
   * Allowed entity type pairs:
   * - AR_STAGED_SALES_ORDER → INBOUND_EMAIL (source email for staged order)
   * - AR_STAGED_SALES_ORDER → AR_SALES_ORDER (created sales order)
   * - AR_STAGED_SALES_ORDER → AR_CUSTOMER (associated customer)
   * - AR_SALES_ORDER → INBOUND_EMAIL
   * - AR_SALES_ORDER → AR_CUSTOMER
   * - AP_INVOICE → INBOUND_EMAIL
   * - AP_INVOICE → AP_SUPPLIER
   */
  ACTIVITY_GRAPH = "ACTIVITY_GRAPH",

  /**
   * Task Execution
   *
   * Links an agent run to a task it worked on.
   *
   * Allowed entity type pairs:
   * - AI_AGENT_RUN → TASK (agent run worked on this task)
   */
  TASK_EXECUTION = "TASK_EXECUTION",

  /**
   * Collection Thread Evidence
   *
   * Links a collection thread to pieces of evidence (invoices, payments,
   * remittance advices, shipments, documents, inbound emails) gathered to
   * support the dispute / follow-up. Source is always the thread; targets
   * are the evidence rows.
   *
   * Allowed entity type pairs (source is always AR_COLLECTION_THREAD):
   * - AR_COLLECTION_THREAD → AR_INVOICE
   * - AR_COLLECTION_THREAD → AR_PAYMENT
   * - AR_COLLECTION_THREAD → AR_PAYMENT_APPLICATION
   * - AR_COLLECTION_THREAD → AR_REMITTANCE_ADVICE
   * - AR_COLLECTION_THREAD → AR_SHIPMENT
   * - AR_COLLECTION_THREAD → INBOUND_EMAIL
   * - AR_COLLECTION_THREAD → CORE_DOCUMENT
   */
  COLLECTION_THREAD_EVIDENCE = "COLLECTION_THREAD_EVIDENCE",

  /**
   * AR Invoice Related
   *
   * Label for the related entities surfaced as tabs on the AR invoice detail
   * page. These associations are DERIVED on read from existing foreign keys
   * (invoice → sales order → shipment, invoice → payment applications →
   * payments, invoice → emails) and are NOT persisted as rows — so they
   * bypass the create-time ASSOCIATION_TYPE_RULES validation. The value is
   * purely a descriptive marker on the derived list returned by
   * `entityAssociations.list` for an AR_INVOICE source.
   *
   * Source is always AR_INVOICE; derived targets:
   * - AR_INVOICE → AR_SALES_ORDER
   * - AR_INVOICE → SCM_SHIPMENT (via SalesOrder.shipmentId)
   * - AR_INVOICE → AR_PAYMENT
   * - AR_INVOICE → OUTBOUND_EMAIL
   * - AR_INVOICE → INBOUND_EMAIL
   */
  AR_INVOICE_RELATED = "AR_INVOICE_RELATED",

  /**
   * Procurement Fulfillment
   *
   * Links a replenishment-created supply order (a purchase order or transfer
   * order) to the sales-order demand it fulfills. Written when the AS400 ack
   * creates the real order (Model D round trip — see
   * `docs/procurement/po-to-write-back.md`), and read by the SO/TO procurement
   * status pages to surface dependent / served / sibling orders.
   *
   * Convention: source is the supply order, target is the demand.
   * Allowed entity type pairs:
   * - INTERNAL_PURCHASE_ORDER → AR_SALES_ORDER (PO fulfills SO)
   * - IM_TRANSFER_ORDER → AR_SALES_ORDER (TO fulfills SO)
   */
  PROCUREMENT_FULFILLMENT = "PROCUREMENT_FULFILLMENT",
}
